'use client';

import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import WhatsAppIcon from '@/components/icons/share/WhatsAppIcon';
import MessengerIcon from '@/components/icons/share/MessengerIcon';
import ShareIcon from '@/components/icons/share/ShareIcon';
import GmailIcon from '@/components/icons/share/GmailIcon';
import LinkedInShareIcon from '@/components/icons/share/LinkedInShareIcon';

interface ShareCTAProps {
  overline?: string;
  heading?: string;
}

export const SHARE_CTA_COMPONENT_NAME = 'ShareCTA';

interface ShareItem {
  name: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  getHref: (url: string, title: string) => string;
  useNativeShare?: boolean;
}

const shareConfig: ShareItem[] = [
  {
    name: 'Share',
    Icon: ShareIcon,
    getHref: () => '#',
    useNativeShare: true,
  },
  {
    name: 'WhatsApp',
    Icon: WhatsAppIcon,
    getHref: (url, title) => `https://wa.me/?text=${title}%20${url}`,
  },
  {
    name: 'Messenger',
    Icon: MessengerIcon,
    getHref: (url) => `https://www.facebook.com/dialog/send?link=${url}&app_id=291494419107518&redirect_uri=${url}`,
  },
  {
    name: 'Gmail',
    Icon: GmailIcon,
    getHref: (url, title) => `https://mail.google.com/mail/?view=cm&fs=1&su=${title}&body=${url}`,
  },
  {
    name: 'LinkedIn',
    Icon: LinkedInShareIcon,
    getHref: (url) => `https://www.linkedin.com/shareArticle?mini=true&url=${url}`,
  },
];

export const ShareCTA = ({
  overline = 'Found this useful?',
  heading = 'Share Tempcover with a friend or save for later',
}: ShareCTAProps) => {
  const [url, setUrl] = useState<string>('');
  const [pageTitle, setPageTitle] = useState<string>('');

  useEffect(() => {
    setUrl(encodeURIComponent(window.location.href));
    setPageTitle(encodeURIComponent(document.title));
  }, []);

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({
        title: decodeURIComponent(pageTitle),
        url: decodeURIComponent(url),
      });
    }
  };

  return (
    <section className="bg-[#59214d] py-[24px] md:py-[32px]">
      <div className="tc-container flex flex-col gap-[24px] md:gap-[32px]">
        {overline && (
          <p className="text-neutral mb-0 max-w-[600px] text-[18px] leading-[24px] font-bold md:text-[22px] md:leading-[32px]">
            {overline}
          </p>
        )}
        {heading && (
          <h2 className="tc-headline text-neutral mt-0 mb-0 max-w-[600px] text-[43px] leading-[1.2] md:text-[50px] lg:text-[55px]">
            {heading}
          </h2>
        )}

        <div className="flex items-center gap-[20px] md:gap-[60px]">
          {shareConfig.map((item) => {
            const { Icon } = item;
            const icon = <Icon className="size-[46px] md:size-[74px]" aria-hidden="true" />;

            if (item.useNativeShare) {
              return (
                <button
                  key={item.name}
                  type="button"
                  onClick={handleNativeShare}
                  aria-label="Share this page"
                  className="shrink-0 cursor-pointer transition-opacity hover:opacity-80"
                >
                  {icon}
                </button>
              );
            }

            return (
              <a
                key={item.name}
                href={item.getHref(url, pageTitle)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Share on ${item.name}`}
                className="shrink-0 transition-opacity hover:opacity-80"
              >
                {icon}
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
